import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { money, newIdempotencyKey } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Checkbox, Field, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { formatQty, invalidateStockControl, parseQty, signedMoney } from './stock-control-common';
import type { CloseSheetResult, StockTakeDetail, StockTakeLine } from './stock-control-types';
import { disputedOf, disputesVisible, isStockApprover } from './stock-take-access';

export function SettleLineDialog({
  takeId,
  line,
  onClose,
}: {
  takeId: string;
  line: StockTakeLine;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [qtyText, setQtyText] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const qty = parseQty(qtyText);
  const qtyIsValid = qty !== null && qty >= 0;

  const settle = useMutation({
    mutationFn: () =>
      post(`/pharmacy/stock-takes/${takeId}/lines/${line.id}/resolve`, { qty, note: note.trim() || undefined }),
    onSuccess: () => {
      toast.success(tr('pharmacy', 'line_settled'), tr('pharmacy', 'x_batch_x_counted_as_x', { genericName: line.product.genericName, batchNo: line.batchNo, qty: formatQty(qty) }));
      void invalidateStockControl(queryClient);
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('pharmacy', 'settle_a_disputed_count')}
      description={`${line.product.genericName} ${line.product.strength ?? ''} · batch ${line.batchNo}`.trim()}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={settle.isPending}>
            {tr('pharmacy', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              settle.mutate();
            }}
            loading={settle.isPending}
            disabled={!qtyIsValid}
          >
            {tr('pharmacy', 'settle')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <p className="text-sm font-medium">{tr('pharmacy', 'what_each_counter_found')}</p>
          <div className="flex flex-wrap gap-2">
            {line.counts.map((entry) => (
              <Button key={entry.userId} variant="outline" size="sm" onClick={() => setQtyText(String(entry.qty))}>
                {entry.userName}: {formatQty(entry.qty)}
              </Button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">{tr('pharmacy', 'choose_one_to_copy_it_or_recount_the_shelf_and')}</p>
        </div>
        <Field label={tr('pharmacy', 'settled_quantity')} required>
          <Input
            type="number"
            min={0}
            step="any"
            className="tabular"
            value={qtyText}
            onChange={(event) => setQtyText(event.target.value)}
          />
        </Field>
        <Field label={tr('pharmacy', 'note')} hint={tr('pharmacy', 'optional_why_this_figure_was_chosen')}>
          <Textarea rows={2} maxLength={240} value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

export function CloseSheetDialog({
  sheet,
  unsavedCount,
  onClose,
}: {
  sheet: StockTakeDetail;
  unsavedCount: number;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [idempotencyKey] = useState(() => newIdempotencyKey());
  const [allowUncounted, setAllowUncounted] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { can } = useAuth();
  const { pending } = sheet.progress;
  const disputed = disputedOf(sheet.progress);
  const approver = isStockApprover(can);
  const showDisputes = disputesVisible(sheet, approver);

  const close = useMutation({
    mutationFn: () =>
      post<CloseSheetResult>(
        `/pharmacy/stock-takes/${sheet.id}/close`,
        { allowUncounted, notes: notes.trim() || undefined },
        { idempotencyKey },
      ),
    onSuccess: (result) => {
      const outcome = result.adjustment;
      if (outcome === null) {
        toast.success(tr('pharmacy', 'x_closed', { number: result.number }), tr('pharmacy', 'the_count_matched_the_books_so_there_was'));
      } else if (outcome.status === 'applied') {
        toast.success(tr('pharmacy', 'x_closed', { number: result.number }), tr('pharmacy', 'differences_posted_net_x_at_cost', { netValueMinor: signedMoney(outcome.netValueMinor) }));
      } else {
        toast.warning(
          `${result.number} closed. Differences sent for approval as ${outcome.number ?? 'a new request'}`,
          tr('pharmacy', 'worth_x_at_cost_the_stock_changes_once_an', { valueMinor: money(outcome.valueMinor) }),
        );
      }
      void invalidateStockControl(queryClient);
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const blockers: string[] = [];
  if (unsavedCount > 0) blockers.push(`${unsavedCount} count${unsavedCount === 1 ? '' : 's'} on screen not saved yet`);
  if (disputed > 0) {
    const lineText = `${disputed} disputed line${disputed === 1 ? '' : 's'}`;
    blockers.push(approver ? `${lineText} still to settle` : `${lineText} for an approver to settle`);
  }
  if (pending > 0 && !allowUncounted) blockers.push(`${pending} line${pending === 1 ? '' : 's'} not counted`);

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('pharmacy', 'close_x', { number: sheet.number })}
      description={tr('pharmacy', 'posts_the_difference_between_what_was_counted')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={close.isPending}>
            {tr('pharmacy', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              close.mutate();
            }}
            loading={close.isPending}
            disabled={blockers.length > 0}
          >
            {tr('pharmacy', 'close_sheet')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {sheet.progress.counted} {tr('pharmacy', 'counted')} {pending} {tr('pharmacy', 'not_counted')}{showDisputes ? ` · ${disputed} disputed` : ''}{tr('pharmacy', 'a_sheet_cannot_be_reopened_once_it_is_closed')}
        </p>
        {pending > 0 && (
          <Checkbox
            checked={allowUncounted}
            onCheckedChange={setAllowUncounted}
            label={tr('pharmacy', 'allow_uncounted_lines')}
            description={tr('pharmacy', 'close_anyway_lines_nobody_counted_are_left_out')}
          />
        )}
        <Field label={tr('pharmacy', 'notes')} hint={tr('pharmacy', 'optional')}>
          <Textarea rows={2} maxLength={600} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </Field>
        {blockers.length > 0 && <Alert variant="warning">{tr('pharmacy', 'before_closing')} {blockers.join(', ')}.</Alert>}
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

export function CancelSheetDialog({ sheet, onClose }: { sheet: StockTakeDetail; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const cancel = useMutation({
    mutationFn: () => post(`/pharmacy/stock-takes/${sheet.id}/cancel`),
    onSuccess: () => {
      toast.success(tr('pharmacy', 'x_cancelled', { number: sheet.number }), tr('pharmacy', 'nothing_was_adjusted'));
      void invalidateStockControl(queryClient);
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('pharmacy', 'cancel_x', { number: sheet.number })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={cancel.isPending}>
            {tr('pharmacy', 'keep_counting')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              setError(null);
              cancel.mutate();
            }}
            loading={cancel.isPending}
          >
            {tr('pharmacy', 'cancel_sheet')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {tr('pharmacy', 'the_sheet_is_abandoned_and_nothing_is_adjusted')}
        </p>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
